import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faClock, faCheckCircle, faTimesCircle, faFlagCheckered } from '@/fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { RecommendationStats, SwingTradeState, LongTermState } from '@/models'

// Simplified trade interface for dashboard display
interface SimplifiedTrade {
  id: string
  state: SwingTradeState | LongTermState
  winLossPercentage?: number
}

export interface HistoricalStats {
  averageYield: number
  winRate: number
  averageHoldingDays: number
  bestYield: number
  worstYield: number
}

type ViewMode = 'current' | 'historical'

interface StatCardWithBlocksProps {
  title: string
  icon: IconDefinition
  openTrades: SimplifiedTrade[]
  finishedTrades: SimplifiedTrade[]
  stats: RecommendationStats | null
  statsLoading?: boolean
  loading: boolean
  onClick: () => void
  dataTour?: string
  defaultView?: ViewMode
  historicalStats?: HistoricalStats | null
}

type ProfitCategory = 'excellent' | 'good' | 'neutral' | 'warning' | 'loss'

interface TradeWithCategory extends SimplifiedTrade {
  category: ProfitCategory
}

const categorizeByProfit = (trade: SimplifiedTrade): ProfitCategory => {
  const percentage = (trade.winLossPercentage ?? 0) * 100

  if (percentage > 15) return 'excellent'
  if (percentage > 0) return 'good'
  if (percentage > -5) return 'neutral'
  if (percentage > -15) return 'warning'
  return 'loss'
}

const getCategoryColor = (category: ProfitCategory) => {
  switch (category) {
    case 'excellent':
      return 'bg-green-600 dark:bg-green-500'
    case 'good':
      return 'bg-green-300 dark:bg-green-400'
    case 'neutral':
      return 'bg-gray-400 dark:bg-gray-500'
    case 'warning':
      return 'bg-yellow-500 dark:bg-yellow-400'
    case 'loss':
      return 'bg-red-500 dark:bg-red-400'
  }
}

const formatPercentage = (value: number | undefined): string => {
  if (value === undefined) return '0%'
  const percentageValue = value * 100
  const sign = percentageValue > 0 ? '+' : ''
  return `${sign}${percentageValue.toFixed(2)}%`
}

const formatYieldValue = (value: number): string => {
  const percentageValue = value * 100
  const sign = percentageValue > 0 ? '+' : ''
  return `${sign}${percentageValue.toFixed(2)}%`
}

const formatDaysValue = (days: number): string => {
  if (days < 1) return '< 1 Tag'
  if (days === 1) return '1 Tag'
  return `${Math.round(days)} Tage`
}

const getYieldColor = (value: number): string => {
  if (value > 0) return 'text-green-600 dark:text-green-400'
  if (value < 0) return 'text-red-600 dark:text-red-400'
  return 'text-gray-600 dark:text-gray-400'
}

function HistoricalStatsView({ historicalStats }: { historicalStats: HistoricalStats | null | undefined }) {
  if (historicalStats === undefined) {
    return (
      <div className="h-[140px] flex items-center justify-center">
        <p className="text-gray-400 dark:text-gray-500 text-sm">Statistiken werden geladen...</p>
      </div>
    )
  }

  if (historicalStats === null) {
    return (
      <div className="h-[140px] flex items-center justify-center">
        <p className="text-gray-400 dark:text-gray-500 text-sm">Keine Statistik-Daten vorhanden</p>
      </div>
    )
  }

  const rows = [
    { label: 'Ø Rendite', value: formatYieldValue(historicalStats.averageYield), color: getYieldColor(historicalStats.averageYield) },
    { label: 'Gewinnquote', value: `${(historicalStats.winRate * 100).toFixed(1)}%`, color: 'text-gray-900 dark:text-white' },
    { label: 'Ø Haltedauer', value: formatDaysValue(historicalStats.averageHoldingDays), color: 'text-gray-900 dark:text-white' },
    { label: 'Beste Perf.', value: formatYieldValue(historicalStats.bestYield), color: 'text-green-600 dark:text-green-400' },
    { label: 'Schlechteste Perf.', value: formatYieldValue(historicalStats.worstYield), color: getYieldColor(historicalStats.worstYield) },
  ]

  return (
    <div className="h-[140px] flex flex-col justify-center space-y-1.5">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">{row.label}</span>
          <span className={`font-semibold ${row.color}`}>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

export default function StatCardWithBlocks({
  title,
  icon,
  openTrades,
  finishedTrades,
  stats,
  loading,
  onClick,
  dataTour,
  defaultView = 'current',
  historicalStats
}: StatCardWithBlocksProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView)

  // Select trades based on view mode
  let trades = viewMode === 'current' ? openTrades : finishedTrades

  // Validate trades prop
  if (!trades || !Array.isArray(trades)) {
    trades = []
  }

  // Validate and filter trades to ensure only valid SimplifiedTrade objects
  const validTrades = trades.filter(trade => {
    if (!trade || typeof trade !== 'object') return false;
    // Must have id
    return 'id' in trade;
  });

  // Categorize trades - only keep the properties we need
  const categorizedTrades: TradeWithCategory[] = validTrades.map(trade => {
    // Explicitly create new object with only allowed properties
    const simplified: SimplifiedTrade = {
      id: trade.id,
      state: trade.state,
      winLossPercentage: trade.winLossPercentage
    };
    return {
      ...simplified,
      category: categorizeByProfit(simplified)
    };
  })

  const handleToggle = (e: React.MouseEvent, mode: ViewMode) => {
    e.stopPropagation() // Prevent card click when toggling
    setViewMode(mode)
  }

  // Stats from props (fetched from endpoint)
  const openCount = stats?.open ?? 0
  const successCount = stats?.success ?? 0
  const failedCount = stats?.failed ?? 0

  if (loading) {
    return (
      <div data-tour={dataTour} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 min-h-80 overflow-hidden flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  // Toggle buttons component
  const ToggleButtons = () => (
    <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
      <button
        onClick={(e) => handleToggle(e, 'current')}
        title="Aktuelle Trades"
        className={`p-1.5 rounded-md transition-colors cursor-pointer ${
          viewMode === 'current'
            ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
        }`}
      >
        <FontAwesomeIcon icon={faClock} className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={(e) => handleToggle(e, 'historical')}
        title="Abgeschlossene Trades"
        className={`p-1.5 rounded-md transition-colors cursor-pointer ${
          viewMode === 'historical'
            ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
        }`}
      >
        <FontAwesomeIcon icon={faFlagCheckered} className="w-3.5 h-3.5" />
      </button>
    </div>
  )

  if (trades.length === 0 && viewMode === 'current') {
    return (
      <div
        data-tour={dataTour}
        onClick={onClick}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 min-h-80 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
      >
      {/* Top section - grows to push stats down */}
      <div className="flex-1">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex-1 min-w-0 flex items-center space-x-2">
            <div className="shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={icon} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h3>
          </div>
          <ToggleButtons />
        </div>

        {/* Empty area - gleiche Höhe wie Blocks Grid */}
        <div className="h-[140px] flex items-center justify-center">
          <p className="text-gray-400 dark:text-gray-600 text-sm">Keine offenen Trades</p>
        </div>
      </div>

      {/* Stats - immer sichtbar */}
        <div className="flex flex-col space-y-2 pt-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-blue-600" />
              <span className="text-gray-600 dark:text-gray-400">Offen</span>
            </div>
            <span className="font-semibold text-blue-600 dark:text-blue-400">{openCount}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 text-lime-600" />
              <span className="text-gray-600 dark:text-gray-400">Erfolgreich-Beendet</span>
            </div>
            <span className="font-semibold text-green-600 dark:text-green-400">{successCount}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faTimesCircle} className="w-3 h-3 text-gray-600" />
              <span className="text-gray-600 dark:text-gray-400">Mit-Verlust-Beendet</span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">{failedCount}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      data-tour={dataTour}
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 min-h-80 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
    >
      {/* Top section - grows to push stats down */}
      <div className="flex-1">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex-1 min-w-0 flex items-center space-x-2">
            <div className="shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={icon} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h3>
          </div>
          <ToggleButtons />
        </div>

        {/* Content area */}
        {viewMode === 'historical' ? (
          <HistoricalStatsView historicalStats={historicalStats} />
        ) : (
          <div className="h-[140px]">
            <div className="flex items-center gap-1.5 mb-2">
              <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400">Offene Trades</span>
            </div>
            <div className="grid grid-cols-8 gap-1 h-[calc(100%-24px)]">
              {categorizedTrades.slice(0, 32).map((trade, index) => (
                <div
                  key={trade.id || index}
                  className={`${getCategoryColor(trade.category)} rounded-sm relative group`}
                  title={`G/V: ${formatPercentage(trade.winLossPercentage)}`}
                />
              ))}
              {/* Fill remaining spaces if less than 32 trades */}
              {categorizedTrades.length < 32 && Array.from({ length: 32 - categorizedTrades.length }).map((_, index) => (
                <div
                  key={`empty-${index}`}
                  className="bg-gray-100 dark:bg-gray-700 rounded-sm opacity-30"
                />
              ))}
            </div>
            {categorizedTrades.length > 32 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                +{categorizedTrades.length - 32} weitere Trades
              </p>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex flex-col space-y-2 pt-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-blue-600" />
            <span className="text-gray-600 dark:text-gray-400">Offen</span>
          </div>
          <span className="font-semibold text-blue-600 dark:text-blue-400">{openCount}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 text-lime-600" />
            <span className="text-gray-600 dark:text-gray-400">Erfolgreich-Beendet</span>
          </div>
          <span className="font-semibold text-green-600 dark:text-green-400">{successCount}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faTimesCircle} className="w-3 h-3 text-gray-600" />
            <span className="text-gray-600 dark:text-gray-400">Mit-Verlust-Beendet</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white">{failedCount}</span>
        </div>
      </div>
    </div>
  )
}
